import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useGeneralSettings } from '../contexts/GeneralSettingsContext';
import { useUserDBContext } from '../contexts/UserDBContext';
import { SUBSCRIPTION_ACCESS } from '../constants/subscriptionRules';
import { useTranslation } from 'react-i18next';
import { FaSave, FaLock } from 'react-icons/fa';
import './style/GeneralSettings.css';
import LoadingLogo from '../components/LoadingLogo';

const Row = ({ title, desc, control, locked }) => (
  <div className={`gs-row ${locked ? 'gs-row-locked' : ''}`}>
    <div className="gs-row-text">
      <div className="gs-row-title">
        {title} {locked && <FaLock className="gs-lock" />}
      </div>
      {desc && <div className="gs-row-desc">{desc}</div>}
    </div>
    <div className="gs-row-control">{control}</div>
  </div>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <label className={`gs-switch ${disabled ? 'disabled' : ''}`}>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
    />
    <span className="gs-slider" />
  </label>
);

const PillNumber = ({ value, onChange, suffix, min = 0, disabled }) => (
  <div className={`gs-pill-number ${disabled ? 'disabled' : ''}`}>
    <input
      type="number"
      min={min}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value || '0', 10))}
      onFocus={(e) => e.target.select()}
      disabled={disabled}
    />
    {suffix && <span className="gs-pill-suffix">{suffix}</span>}
  </div>
);

const BigColor = ({ value, onChange, disabled }) => (
  <label className={`gs-color-wrap ${disabled ? 'disabled' : ''}`}>
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    />
    <span className="gs-color-box" style={{ background: value }} />
    <span className="gs-color-value">{value}</span>
  </label>
);

export default function GeneralSettings() {
  const { t } = useTranslation();
  const { generalSettings, saveGeneralSettings, loadingSettings, DEFAULT_SETTINGS } = useGeneralSettings();
  const { userDBData } = useUserDBContext();
  const subscription = userDBData?.subscriptions || 'P1';

  const [localSettings, setLocalSettings] = useState(DEFAULT_SETTINGS);
  const [activeTab, setActiveTab] = useState('appearance'); // appearance | photowall
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null

  useEffect(() => setLocalSettings(generalSettings), [generalSettings]);

  const hasAccess = (key) => SUBSCRIPTION_ACCESS[key]?.includes(subscription);
  const up = (k, v) => setLocalSettings((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    setSaveStatus(null);
    const res = await saveGeneralSettings(localSettings);
    if (res.success) {
      setSaveStatus('success');
      setSaveMessage(t('generalSettings.saveSuccess') || 'Settings saved successfully');
      setTimeout(() => {
        setSaveMessage('');
        setSaveStatus(null);
      }, 2500);
    } else {
      setSaveStatus('error');
      setSaveMessage(t('generalSettings.saveError') || 'Error saving settings');
    }
    setSaving(false);
  };

  // Loader
  if (loadingSettings || !generalSettings || Object.keys(generalSettings || {}).length === 0) {
    return <LoadingLogo />;
  }

  // Valeurs
  const {
    fontColor = '#ffffff',
    fontFamily = 'Arial',
    fontSize = '24px',
    animationEnabled = false,
    animationType = 'fade',
    transitionSpeed = 5,
    showScanMe = true,
    scanInterval = 5,
    showQrCorner = false,
    qrCornerPosition = 'bottom-right',
    qrCornerSize = 80,
    bannerEnabled = false,
    bannerPosition = 'hautcentre',
    bannerText = '',
    bannerTextColor = '#ffffff',
    bannerFontSize = '24px',
    watermarkEnabled = true,
    bgColor = '#000000',
    backgroundImage = '',
  } = localSettings;

  return (
    <>
      <Helmet>
        <title>General Settings • BeOnWall</title>
        <meta name="description" content="Configure your default settings for new events and the diaporama." />
      </Helmet>

      <div className="general-settings-page">
        {/* Titre */}
        <div className="gs-header">
          <h1 className="gs-title">{t('generalSettings.title') || 'General Settings'}</h1>
        </div>

        {/* Info */}
        <p className="gs-info-text">
          {t('generalSettings.info')}
        </p>

        {/* Topbar : Tabs + Save */}
        <div className="gs-topbar">
          <div className="gs-tabs">
            <button
              className={`gs-tab ${activeTab === 'appearance' ? 'active' : ''}`}
              onClick={() => setActiveTab('appearance')}
            >
              {t('generalSettings.appearance') || 'Appearance'}
            </button>
            <button
              className={`gs-tab ${activeTab === 'photowall' ? 'active' : ''}`}
              onClick={() => setActiveTab('photowall')}
            >
              {t('generalSettings.photoWall') || 'Photo Wall'}
            </button>
          </div>

          <div className="gs-actions">
            <button className="gs-btn gs-btn-gold" onClick={handleSave} disabled={saving}>
              <FaSave /> {saving ? (t('generalSettings.saving') || 'Saving...') : (t('generalSettings.save') || 'Save')}
            </button>
          </div>
        </div>

        {/* Toast */}
        {saveMessage && (
          <div className={`gs-toast ${saveStatus === 'success' ? 'success' : 'error'}`}>
            {saveMessage}
          </div>
        )}

        {/* --- APPEARANCE --- */}
        {activeTab === 'appearance' && (
          <div className="gs-panel">
            <Row
              title={t('eventSettings.text') || 'Text Color'}
              control={
                <BigColor
                  value={fontColor}
                  onChange={(v) => up('fontColor', v)}
                  disabled={!hasAccess('text')}
                />
              }
              locked={!hasAccess('text')}
            />

            <Row
              title={t('generalSettings.bgColor') || 'Background Color'}
              control={
                <BigColor
                  value={bgColor}
                  onChange={(v) => up('bgColor', v)}
                  disabled={!hasAccess('fond')}
                />
              }
              locked={!hasAccess('fond')}
            />

            <Row
              title={t('eventSettings.policy') || 'Font Family'}
              control={
                <select
                  className="gs-input"
                  value={fontFamily}
                  onChange={(e) => up('fontFamily', e.target.value)}
                  disabled={!hasAccess('font')}
                >
                  <option value="Arial">Arial</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                  <option value="'Courier New', monospace">Courier</option>
                </select>
              }
              locked={!hasAccess('font')}
            />

            <Row
              title={t('eventSettings.size') || 'Font Size'}
              control={
                <PillNumber
                  value={parseInt(fontSize, 10)}
                  onChange={(v) => up('fontSize', `${v}px`)}
                  suffix="px"
                />
              }
              locked={!hasAccess('taille')}
            />

            <Row
              title={t('eventSettings.watermark') || 'Watermark'}
              control={
                <Toggle
                  checked={watermarkEnabled}
                  onChange={(v) => up('watermarkEnabled', v)}
                  disabled={!hasAccess('watermark')}
                />
              }
              locked={!hasAccess('watermark')}
            />

            <Row
              title={t('generalSettings.backgroundImage') || 'Background Image'}
              desc={backgroundImage ? backgroundImage : ''}
              control={
                <button
                  type="button"
                  className="gs-btn"
                  onClick={() => alert('Background upload UI à brancher')}
                  disabled={!hasAccess('background')}
                >
                  {t('generalSettings.upload') || 'Upload'}
                </button>
              }
              locked={!hasAccess('background')}
            />
          </div>
        )}

        {/* --- PHOTO WALL --- */}
        {activeTab === 'photowall' && (
          <div className="gs-panel">
            {/* Animation */}
            <Row
              title={t('eventSettings.animation') || 'Animation'}
              control={
                <div className="gs-control">
                  <Toggle
                    checked={animationEnabled}
                    onChange={(v) => up('animationEnabled', v)}
                    disabled={!hasAccess('animation')}
                  />
                  {animationEnabled && (
                    <div className="gs-subcontrols">
                      <select
                        className="gs-input"
                        value={animationType}
                        onChange={(e) => up('animationType', e.target.value)}
                      >
                        <option value="fade">{t('eventSettings.fade') || 'Fade'}</option>
                        <option value="slide-left">{t('eventSettings.slideLeft') || 'Slide Left'}</option>
                        <option value="slide-right">{t('eventSettings.slideRight') || 'Slide Right'}</option>
                        <option value="zoom">{t('eventSettings.zoom') || 'Zoom'}</option>
                      </select>
                    </div>
                  )}
                </div>
              }
              locked={!hasAccess('animation')}
            />

            {/* Transition */}
            <Row
              title={t('eventSettings.transition') || 'Transition Speed'}
              control={
                <PillNumber
                  value={transitionSpeed}
                  onChange={(v) => up('transitionSpeed', v)}
                  suffix={t('generalSettings.seconds') || 'seconds'}
                  min={1}
                />
              }
              locked={!hasAccess('transition')}
            />

            {/* Scan Me */}
            <Row
              title="Scan Me"
              control={
                <div className="gs-control">
                  <Toggle
                    checked={showScanMe}
                    onChange={(v) => up('showScanMe', v)}
                    disabled={!hasAccess('transition')}
                  />
                  {showScanMe && (
                    <div className="gs-subcontrols">
                      <PillNumber
                        value={scanInterval}
                        onChange={(v) => up('scanInterval', v)}
                        suffix={t('generalSettings.slides') || 'slides'}
                        min={1}
                      />
                    </div>
                  )}
                </div>
              }
              locked={!hasAccess('transition')}
            />

            {/* QR Code */}
            <Row
              title={t('eventSettings.qrCode') || 'QR Code'}
              control={
                <div className="gs-control">
                  <Toggle
                    checked={showQrCorner}
                    onChange={(v) => up('showQrCorner', v)}
                    disabled={!hasAccess('qr')}
                  />
                  {showQrCorner && (
                    <div className="gs-subcontrols">
                      <select
                        className="gs-input"
                        value={qrCornerPosition}
                        onChange={(e) => up('qrCornerPosition', e.target.value)}
                      >
                        <option value="bottom-right">{t('eventSettings.bottomRight') || 'Bottom Right'}</option>
                        <option value="bottom-left">{t('eventSettings.bottomLeft') || 'Bottom Left'}</option>
                      </select>
                      <PillNumber
                        value={qrCornerSize}
                        onChange={(v) => up('qrCornerSize', v)}
                        suffix="px"
                        min={40}
                      />
                    </div>
                  )}
                </div>
              }
              locked={!hasAccess('qr')}
            />

            {/* Banner */}
            <Row
              title={t('eventSettings.banner') || 'Banner'}
              control={
                <div className="gs-control">
                  <Toggle
                    checked={bannerEnabled}
                    onChange={(v) => up('bannerEnabled', v)}
                    disabled={!hasAccess('banner')}
                  />
                  {bannerEnabled && (
                    <div className="gs-subcontrols">
                      <select
                        className="gs-input"
                        value={bannerPosition}
                        onChange={(e) => up('bannerPosition', e.target.value)}
                      >
                        <option value="hautcentre">{t('eventSettings.topCenter') || 'Top Center'}</option>
                        <option value="bascentre">{t('eventSettings.bottomCenter') || 'Bottom Center'}</option>
                      </select>
                      <input
                        className="gs-input"
                        type="text"
                        placeholder={t('eventSettings.text') || 'Text'}
                        value={bannerText}
                        onChange={(e) => up('bannerText', e.target.value)}
                      />
                      <BigColor
                        value={bannerTextColor}
                        onChange={(v) => up('bannerTextColor', v)}
                      />
                      <PillNumber
                        value={parseInt(bannerFontSize, 10)}
                        onChange={(v) => up('bannerFontSize', `${v}px`)}
                        suffix="px"
                        min={8}
                      />
                    </div>
                  )}
                </div>
              }
              locked={!hasAccess('banner')}
            />
          </div>
        )}
      </div>
    </>
  );
}
